import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaEdit } from '@react-icons/all-files/fa/FaEdit';
import { FaRegCalendarCheck } from '@react-icons/all-files/fa/FaRegCalendarCheck';
import { FaCalendarAlt } from '@react-icons/all-files/fa/FaCalendarAlt';
import { FaCog } from '@react-icons/all-files/fa/FaCog';
import { FaHome } from '@react-icons/all-files/fa/FaHome';
import { FaPlus } from '@react-icons/all-files/fa/FaPlus';
import Breadcrumb from '../../../../commons/Breadcrumb';
import PageHeader from '../../../../commons/PageHeader';
import PageAlert, { usePageAlert } from '../../../../commons/PageAlert';
import Confirm, { useConfirm } from '../../../../commons/Confirm';
import { EditButton, DeleteButton, RenderActions } from '../../../../commons/DataGridActions';
import ProfaseDataGrid from '../../../../commons/Datagrid';
import { useAppDispatch, useAppSelector } from '../../../../store';
import { getEntities, deleteEntity, reset } from '../../../../manager/profase-periodo-actividad.reducer';
import {
  formatTipoPeriodoLabel,
  TIPO_PERIODO_OPTIONS,
} from '../../../../shared/workflow-periodicidad';

const BASE = '/configuracion/calendario/periodos/definicion-periodo';
const PERIODOS = '/configuracion/calendario/periodos';

function TipoLabel({ tipo }: { tipo?: string | null }) {
  if (!tipo) return <span style={{ color: '#94a3b8' }}>—</span>;
  return <span>{formatTipoPeriodoLabel(tipo)}</span>;
}

function NumeroPeriodoLabel({ numero }: { numero?: number | null }) {
  if (numero == null) {
    return (
      <span style={{ color: '#64748b', fontSize: 12, fontStyle: 'italic' }}>
        Todos
      </span>
    );
  }
  return <span>{numero}</span>;
}

export default function DefinicionPeriodoPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const topRef = useRef<HTMLDivElement>(null);

  const entities = useAppSelector(s => s.profasePeriodoActividad.entities);
  const totalItems = useAppSelector(s => s.profasePeriodoActividad.totalItems);
  const loading = useAppSelector(s => s.profasePeriodoActividad.loading);
  const updating = useAppSelector(s => s.profasePeriodoActividad.updating);
  const updateSuccess = useAppSelector(s => s.profasePeriodoActividad.updateSuccess);

  const [rows, setRows] = useState<any[]>([]);
  const [lastParams, setLastParams] = useState<any>(null);

  const { alert, showToast } = usePageAlert();
  const { confirmConfig, ask, dismiss, setConfirmLoading } = useConfirm();

  useEffect(() => {
    const msg = (location.state as any)?.successMsg;
    if (msg) { showToast('success', msg); window.history.replaceState({}, ''); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (updateSuccess) {
      dispatch(reset());
      dismiss();
      if (lastParams) dispatch(getEntities(lastParams));
      showToast('success', 'Definición de periodo eliminada correctamente.');
    }
  }, [updateSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setConfirmLoading(updating); }, [updating]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setRows(entities ?? []); }, [entities]);

  const fetchData = (params: any) => {
    setLastParams(params);
    dispatch(getEntities(params));
  };

  const renderActions = (params: any) => (
    <RenderActions>
      <EditButton onClick={() => navigate(`${BASE}/${params.row.id}/edit`)} />
      <DeleteButton onClick={() => {
        ask(
          <>¿Confirmas que deseas eliminar <strong>{params.row.nombre ?? `definición #${params.row.id}`}</strong>? Esta acción no se puede deshacer.</>,
          () => dispatch(deleteEntity(params.row.id)),
          { title: 'Eliminar definición de periodo', confirmLabel: 'Eliminar', variant: 'danger' },
        );
        topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }} />
    </RenderActions>
  );

  const cols = [
    { field: 'id', headerName: 'ID', flex: 0.45 },
    { field: 'nombre', headerName: 'Nombre', flex: 2 },
    {
      field: 'tipoPeriodo',
      headerName: 'Tipo periodo',
      flex: 0.9,
      renderCell: (p: any) => <TipoLabel tipo={p.row.tipoPeriodo} />,
    },
    {
      field: 'numeroPeriodo',
      headerName: 'Número periodo',
      flex: 0.9,
      renderCell: (p: any) => <NumeroPeriodoLabel numero={p.row.numeroPeriodo} />,
    },
    { field: '__actions', headerName: '', flex: 0.75, renderCell: renderActions },
  ];

  const filters = [
    { label: 'Nombre', name: 'nombre' },
    {
      label: 'Tipo periodo',
      name: 'tipoPeriodo',
      type: 'select',
      single: true,
      options: TIPO_PERIODO_OPTIONS,
    },
    { label: 'Número periodo', name: 'numeroPeriodo' },
  ];

  return (
    <div style={g.page} ref={topRef}>
      <Breadcrumb items={[
        { label: 'Inicio', to: '/', icon: <FaHome /> },
        { label: 'Configuración', to: '/configuracion', icon: <FaCog /> },
        { label: 'Calendario', to: '/configuracion/calendario', icon: <FaCalendarAlt /> },
        { label: 'Periodos', to: PERIODOS, icon: <FaRegCalendarCheck /> },
        { label: 'Definición de Periodo', icon: <FaEdit /> },
      ]} />

      <PageHeader
        icon={<FaEdit />}
        iconColor="#10b981"
        title="Definición de Periodo"
        subtitle="Catálogo de tipos de actividad por periodo"
        action={{
          label: <><FaPlus style={{ marginRight: 6 }} />Nueva definición</>,
          onClick: () => navigate(`${BASE}/new`),
          color: '#10b981',
        }}
      />

      <Confirm config={confirmConfig} />
      {alert && <PageAlert alert={alert} />}

      <ProfaseDataGrid
        cols={cols}
        title=""
        filters={filters}
        fetch={fetchData}
        rowCount={totalItems}
        loading={loading}
        rows={rows}
        defaultSortField="nombre"
      />
    </div>
  );
}

const g: Record<string, React.CSSProperties> = {
  page: { display: 'flex', flexDirection: 'column', gap: 24 },
};
